'use client'

import { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { getTaxListByHsCode } from '@/modules/hs/api/hs-tax.api'
import type {
  HsTaxListResponse,
  HsTaxCommonResponse,
} from '@/modules/hs/types/hs-tax.response'
import NKUDDB_FORM from '@/modules/hs/constants/nkuddb-form.json'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
)

interface Props {
  hsCode: string
}

const months = [
  'Tháng 1',
  'Tháng 2',
  'Tháng 3',
  'Tháng 4',
  'Tháng 5',
  'Tháng 6',
  'Tháng 7',
  'Tháng 8',
  'Tháng 9',
  'Tháng 10',
  'Tháng 11',
  'Tháng 12',
]

const colors = {
  nktt: '#3b82f6', // Blue
  nkud: '#ec4899', // Pink
  fta: ['#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f'], // Multiple colors for FTAs
}

const processDataForChart = (
  records: HsTaxCommonResponse[],
  currentYear: number,
) => {
  return months.map((_, monthIndex) => {
    const targetDate = new Date(currentYear, monthIndex, 15) // 15th of each month
    const applicableRecord = records.find((record) => {
      const effectiveFrom = new Date(record.effectiveFrom)
      const effectiveTo = new Date(record.effectiveTo)
      return targetDate >= effectiveFrom && targetDate <= effectiveTo
    })
    return applicableRecord && applicableRecord.taxRate >= 0
      ? applicableRecord.taxRate
      : null
  })
}

const processNKUDDBForChart = (
  taxes: Record<string, HsTaxCommonResponse[]>,
  currentYear: number,
) => {
  const finalTaxes = []
  const taxIds = Object.keys(taxes).sort((a, b) => {
    const aIndex = NKUDDB_FORM.findIndex((item) => item.key === a)
    const bIndex = NKUDDB_FORM.findIndex((item) => item.key === b)
    return aIndex - bIndex
  })

  let colorIndex = 0
  for (const taxId of taxIds) {
    const records = taxes[taxId]
    const data = processDataForChart(records, currentYear)

    // Skip if no data
    if (data.filter((d) => d !== null).length === 0) {
      continue
    }

    const form = NKUDDB_FORM.find((f) => f.key === taxId)
    if (!form) continue

    const color = colors.fta[colorIndex % colors.fta.length]
    colorIndex++

    const label = `${form.tenRutgon}`

    finalTaxes.push({
      label,
      data,
      borderColor: color,
      backgroundColor: color + '20',
      borderWidth: 2,
      tension: 0.1,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: color,
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
    })
  }

  return finalTaxes
}

export default function TaxHistoryChart({ hsCode }: Props) {
  const [taxData, setTaxData] = useState<HsTaxListResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const currentYear = new Date().getFullYear()

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getTaxListByHsCode(hsCode)
        setTaxData(data)
      } catch (error) {
        console.error('Failed to fetch tax data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [hsCode])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[300px]">
        <div className="text-sm text-[#737373]">Đang tải...</div>
      </div>
    )
  }

  if (!taxData) {
    return (
      <div className="flex justify-center items-center h-[300px]">
        <div className="text-sm text-[#737373]">
          Không có dữ liệu lịch sử thuế
        </div>
      </div>
    )
  }

  const datasets = []

  // NKTT dataset
  const nkttData = processDataForChart(taxData.nktt || [], currentYear)
  if (nkttData.filter((d) => d !== null).length > 0) {
    const nkttLabel =
      taxData.nktt?.[0]?.taxTable?.tenBangVn || 'Thuế MFN (NKTT)'
    datasets.push({
      label: nkttLabel,
      data: nkttData,
      borderColor: colors.nktt,
      backgroundColor: colors.nktt + '20',
      borderWidth: 2,
      tension: 0.1,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: colors.nktt,
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
    })
  }

  // NKUD dataset
  const nkudData = processDataForChart(taxData.nkud || [], currentYear)
  if (nkudData.filter((d) => d !== null).length > 0) {
    const nkudLabel =
      taxData.nkud?.[0]?.taxTable?.tenBangVn || 'Thuế ưu đãi (NKUD)'
    datasets.push({
      label: nkudLabel,
      data: nkudData,
      borderColor: colors.nkud,
      backgroundColor: colors.nkud + '20',
      borderWidth: 2,
      tension: 0.1,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: colors.nkud,
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
    })
  }

  // NKUDDB (FTA) datasets
  const nkuddbDatasets = processNKUDDBForChart(
    taxData.nkuddb || {},
    currentYear,
  )
  datasets.push(...nkuddbDatasets)

  // If no datasets have data, show empty state
  if (datasets.length === 0) {
    return (
      <div className="flex justify-center items-center h-[300px]">
        <div className="text-sm text-[#737373]">
          Không có dữ liệu thuế cho năm {currentYear}
        </div>
      </div>
    )
  }

  const chartData = {
    labels: months,
    datasets,
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          padding: 12,
          font: {
            size: 11,
          },
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || ''
            if (label) {
              label += ': '
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y + '%'
            }
            return label
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: false,
        },
        grid: {
          display: true,
          color: '#e5e7eb',
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: function (value: any) {
            return value + '%'
          },
        },
      },
      x: {
        title: {
          display: false,
        },
        grid: {
          display: true,
          color: '#e5e7eb',
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  }

  return (
    <div style={{ height: '600px', padding: '8px 0' }}>
      <Line data={chartData} options={options} />
    </div>
  )
}
